


























































import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import moment from "moment";
import { formatCurrency } from "@/shared/currency";
import _ from "lodash";
import ContributionsPieChart from "@/shared/components/contribution/ContributionsPieChart.vue";
import ContributionsDonutChart from "@/employee/components/contributionsV2/ContributionsDonutChart.vue";
import {
  FundingOption,
  CardWidgetTab,
  PersonalScheduledContribution,
  FeatureFlags
} from "@/shared/types";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { Home, ContributionsV2 } from "@/employee/route-names";
import { PartnerAccountHolderDashboard } from "@/employer/route-names";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";
import { useContributionStore } from "@/shared/store/contribution";
import { useUserStore } from "@/shared/store/user";
import { useHsaStore } from "@/shared/store/hsa";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useHsaLimitStore } from "@/shared/store/hsalimit";

@Component({
  components: {
    GenericLoadingSpinner,
    ContributionsDonutChart,
    ContributionsPieChart
  }
})
export default class ContributionsGraphWidget extends Vue {
  hsaStore = useHsaStore();
  hsaTotalStore = useHsaTotalStore();
  contributionStore = useContributionStore();
  userStore = useUserStore();
  custodianSettingsStore = useCustodianSettingsStore();
  featureFlagStore = useFeatureFlagStore();
  hsaLimitStore = useHsaLimitStore();

  ContributionsV2 = ContributionsV2;
  EmployeeHome = Home;
  EmployerAccountHolderDashboard = PartnerAccountHolderDashboard;

  @Prop()
  width: number;

  @Prop()
  height: number;

  @Prop({ required: false, default: false})
  forecastMode: boolean;

  @Prop({ required: false })
  allowedForecastYears: number[];

  @Prop({ required: false })
  currentForecastYear: number;

  @Prop({ required: false })
  forecastAmount: number;

  @Prop({ required: false, default: false })
  showForecastAmount: boolean;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop( { required: false} )
  employeeAmount: number;

  @Prop({required: false, default: true})
  showYearSelection: boolean;

  @Prop( { required: false} )
  otherHsaAmount: number;

  selectedYear = this.currentYearClock;
  tabIndex = 0;
  formatCurrency = formatCurrency;

  mounted (): void {
    this.loadComponentData();
  }

  handleYearChange (value): void {
    this.selectedYear = value;
    this.loadTransactionTotals(value);

    if (this.forecastMode) {
      if (value !== this.currentForecastYear) {
        this.$emit("forecast-year-changed", value);
      }

      this.$nextTick(() => {
        // If switching to a forecasted year, show the projected tab. Show active otherwise
        this.tabIndex = this.allowedForecastYears.includes(value) ? 1 : 0;
      });
    }
  }

  get activeTabs (): CardWidgetTab[] {
    const tabs = [
      { title: "Actual", disabled: this.actualTabDisabled, visible: true },
      { title: "Projected", disabled: this.projectedTabDisabled, visible: this.projectedTabVisible }
    ];
    return tabs.filter(tab => tab.visible);
  }

  get projectedTabDisabled (): boolean {
    if (this.forecastMode) {
      return !this.showForecast;
    }

    return this.selectedYear < this.hsaTotalStore.activePayrollYear;
  }

  get actualTabDisabled (): boolean {
    return this.selectedYear > this.hsaTotalStore.activePayrollYear;
  }

  get projectedTabVisible (): boolean {
    return this.hasBendProjectedPayroll || this.forecastMode;
  }

  get hasBendProjectedPayroll (): boolean {
    return this.userStore.employeeFundingOption === FundingOption.BendCalculatePull &&
      this.custodianSettingsStore.displayProjectedContributions;
  }

  get showForecast (): boolean {
    // Forecast amount for current or future contributions is shown on the projected tab
    return this.forecastMode && this.allowedForecastYears.includes(this.selectedYear);
  }

  get projectedForecastAmount (): number {
    return this.showForecast ? this.forecastAmount : 0;
  }

  get selectedYearHsaLimit (): number {
    return this.hsaLimitStore.getHsaLimitByYear(this.selectedYear);
  }

  get selectedYearEmployeePostTaxContributions () {
    return this.hsaStore.employeePostTaxContributions(this.selectedYear);
  }

  get currentYearClock () {
    return moment().year();
  }

  yearsThrough (year: number): number[] {
    return _.range(this.contributionStore.firstPlanEffectiveYear, year + 1); // (from,to]
  }

  get contributionYearOptionsAllowingForFuturePayroll (): number[] {
    const nextYear = this.hsaTotalStore.activePayrollYear ?
      this.currentYearClock + 1 : this.hsaTotalStore.activePayrollYear + 1;

    return this.yearsThrough(nextYear);
  }

  get contributionYearOptionsActualOnly () {
    return this.yearsThrough(this.currentYearClock);
  }

  get yearOptions () {
    if (!this.showYearSelection) {
      return null;
    }
    if (this.custodianSettingsStore.isHsaBankCustodian) {
      return this.unfilteredYearOptions.filter(y => y >= 2022);
    }

    return this.unfilteredYearOptions;
  }

  get unfilteredYearOptions () {
    if (this.hasBendProjectedPayroll ||
        (this.forecastMode && this.allowedForecastYears.includes(this.hsaTotalStore.activePayrollYear + 1))) {
      return this.contributionYearOptionsAllowingForFuturePayroll;
    }
    return this.contributionYearOptionsActualOnly;
  }

  get otherHsaContribution (): number {
    if (this.otherHsaAmount || this.otherHsaAmount === 0) {
      return this.otherHsaAmount;
    }
    if (this.selectedYear > this.hsaTotalStore.activePayrollYear) {
      return 0;
    }
    return this.hsaLimitStore.otherHsaAmount(this.selectedYear);
  }

  get employeePersonalContributions (): number {
    const result = this.annualEmployeePersonalContribution;

    // adjust for rounding in floating point precision
    return _.floor(result, 2);
  }

  get bankDirectContribution (): number {
    const result = this.hsaStore.bankDirectContributions(this.selectedYear);

    // adjust for rounding in floating point precision
    return _.floor(result, 2);
  }

  get employeePayrollContributions (): number {
    const result = this.annualEmployeePayrollContribution + this.selectedYearEmployeePostTaxContributions;

    // adjust for rounding in floating point precision
    return _.floor(result, 2);
  }

  get projectedEmployeePayrollContributions (): number {
    if (this.employeeAmount) {
      return this.employeeAmount;
    }

    if (!this.hasBendProjectedPayroll) {
      return this.employeePayrollContributions;
    }

    const result = this.projectedAnnualEmployeePreTaxPayrollContribution +
      this.selectedYearEmployeePostTaxContributions;
    // adjust for rounding in floating point precision
    return _.floor(result, 2);
  }

  get annualEmployeePayrollContribution (): number {
    if (this.selectedYear > this.hsaTotalStore.activePayrollYear) {
      return this.hsaTotalStore.nextYearEmployeeTotal;
    }

    return this.hsaStore.employeePreTaxContributions(this.selectedYear);
  }

  get projectedPersonalContributions (): number {
    return this.hsaStore.totalEmployeePersonalContributions(this.selectedYear);
  }

  get annualEmployeePersonalContribution () {
    if (this.selectedYear > this.hsaTotalStore.activePayrollYear) {
      return 0;
    }

    return this.hsaStore.actualEmployeePersonalContributions(this.selectedYear);
  }

  get annualEmployerContribution (): number {
    if (this.selectedYear > this.hsaTotalStore.activePayrollYear) {
      return this.hsaTotalStore.nextYearEmployerTotal;
    }

    return this.hsaStore.totalEmployerPayrollContributions(this.selectedYear);
  }

  get projectedAnnualEmployeePreTaxPayrollContribution () {
    if (this.selectedYear > this.hsaTotalStore.activePayrollYear) {
      return this.hsaTotalStore.nextYearEmployeeTotal;
    }

    return this.hsaTotalStore.initialAnnualPreTaxContribution;
  }

  get projectedAnnualEmployerContribution () {
    if (!this.hasBendProjectedPayroll) {
      return this.annualEmployerContribution;
    }

    if (this.selectedYear > this.hsaTotalStore.activePayrollYear) {
      return this.hsaTotalStore.nextYearEmployerTotal;
    }

    return this.hsaTotalStore.employerAmount;
  }

  get projectedPersonalScheduledContributions (): number {
    return this.hsaTotalStore.personalScheduledContributionsByYear(this.selectedYear, true);
  }

  loadTransactionTotals (year: number) {
    this.hsaStore.loadTransactionTotals({
      year,
      username: this.username,
      partnerId: this.partnerId
    });
  }

  @Watch("username")
  @Watch("partnerId")
  loadComponentData () {
    const customerServiceArguments = {
      username: this.username,
      partnerId: this.partnerId
    };

    this.hsaTotalStore.getUserPayrollSummary(customerServiceArguments);
    this.hsaLimitStore.getHsaAccountLimits(customerServiceArguments);
    this.contributionStore.getPlanDetails(customerServiceArguments);
    this.handleYearChange(this.selectedYear);
  }

  @Watch("currentForecastYear")
  forecastYearUpdated () {
    if (this.currentForecastYear !== this.selectedYear) {
      this.handleYearChange(this.currentForecastYear);
    }
  }

  get isLoaded () {
    return this.hsaStore.contributionTotalsLoaded(this.selectedYear);
  }

  get showRouterLink () {
    return this.featureFlagStore.flags.bend17151ContributionWidgetUpdate &&
      (this.$route.name === this.EmployeeHome || this.$route.name === PartnerAccountHolderDashboard);
  }

  tabUpdated (value: number) {
    this.tabIndex = value;
  }
}
